import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowRight,
    BadgeCheck,
    CheckCircle2,
    Eye,
    Info,
    LayoutGrid,
    MessageCircle,
    MessagesSquare,
    Minus,
    Phone,
    Plus,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { trackClick } from "@/lib/api";
import { useContact } from "@/lib/contact";
import AmbientGlow from "@/components/AmbientGlow";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_DEMOS_PER_DAY = 5; // mirrors MAX_DEMOS_PER_DAY in backend/server.py

const BENEFITS = [
    { icon: Eye, label: "See the workspace", id: "see" },
    { icon: LayoutGrid, label: "Explore workspace options", id: "explore" },
    { icon: MessagesSquare, label: "Discuss your requirements", id: "discuss" },
    { icon: BadgeCheck, label: "Choose the right monthly plan", id: "choose" },
];

const inputCls =
    "w-full rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3.5 text-sm font-semibold text-ink placeholder:font-medium placeholder:text-ink/35 transition-all duration-300 focus:border-brand-blue/50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand-blue/10";

const labelCls = "mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/50";

const fmtLong = (iso) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", {
        weekday: "short",
        day: "numeric",
        month: "short",
    });

const statusStyle = (status) =>
    status === "full"
        ? "bg-rose-50 text-rose-700"
        : status === "limited"
          ? "bg-amber-50 text-amber-700"
          : "bg-brand-teal/10 text-brand-teal";

export default function DemoBookingSection() {
    const contact = useContact();
    const [form, setForm] = useState({ name: "", company: "", mobile: "", email: "", seats: 1, date: "" });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | submitting | success | duplicate
    const [booked, setBooked] = useState(null);
    const [days, setDays] = useState([]);
    const [dayAvail, setDayAvail] = useState(null);

    const todayIso = new Date().toLocaleDateString("en-CA");

    const refreshDays = () =>
        axios
            .get(`${API}/demo-availability`, { params: { days: 6 } })
            .then((r) => setDays(r.data?.days || []))
            .catch(() => {});

    useEffect(() => {
        refreshDays();
    }, []);

    useEffect(() => {
        if (!form.date) return setDayAvail(null);
        axios
            .get(`${API}/demo-availability`, { params: { date: form.date } })
            .then((r) => setDayAvail(r.data))
            .catch(() => setDayAvail(null));
    }, [form.date]);

    const set = (key, value) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const validate = () => {
        const errs = {};
        const digits = form.mobile.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "");
        if (form.name.trim().length < 2) errs.name = "Please enter your name.";
        if (!/^[6-9]\d{9}$/.test(digits)) errs.mobile = "Enter a valid 10-digit mobile number.";
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email address.";
        if (!form.seats || form.seats < 1) errs.seats = "At least 1 seat is required.";
        if (!form.date) errs.date = "Please choose your demo date.";
        else if (dayAvail?.blocked) errs.date = "We are closed on this date — please choose another day.";
        else if (dayAvail?.status === "full") errs.date = "Demo slots are fully booked for this date — please choose another day.";
        return errs;
    };

    const submit = async (e) => {
        e.preventDefault();
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length) return;
        setStatus("submitting");
        try {
            await axios.post(`${API}/demo-bookings`, {
                name: form.name.trim(),
                company: form.company.trim(),
                mobile: form.mobile,
                email: form.email.trim(),
                seats: Number(form.seats),
                demo_date: form.date,
            });
            setBooked({ name: form.name.trim(), date: form.date, seats: form.seats });
            setStatus("success");
            refreshDays();
        } catch (err) {
            const code = err.response?.data?.detail?.code;
            if (code === "duplicate") {
                setStatus("duplicate");
            } else if (code === "full" || code === "blocked") {
                setStatus("idle");
                setErrors({
                    date: code === "blocked"
                        ? "We are closed on this date — please choose another day."
                        : "Demo slots are fully booked for this date — please choose another day.",
                });
                refreshDays();
                axios.get(`${API}/demo-availability`, { params: { date: form.date } }).then((r) => setDayAvail(r.data)).catch(() => {});
            } else {
                setStatus("idle");
                toast.error("Something went wrong. Please call or WhatsApp us instead.");
            }
        }
    };

    const stepSeats = (delta) =>
        set("seats", Math.min(50, Math.max(1, Number(form.seats || 1) + delta)));

    return (
        <section id="book-demo" data-testid="demo-section" className="cv-auto relative overflow-hidden py-20 sm:py-28">
            <AmbientGlow color="#2B5CAB" side="left" className="left-1/2 top-0 -ml-[360px] h-[420px] w-[720px]" />

            <div className="relative mx-auto grid max-w-6xl grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
                {/* LEFT — editorial content */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: EASE }}
                >
                    <p
                        data-testid="demo-eyebrow"
                        className="inline-flex items-center gap-2.5 rounded-full border border-brand-purple/15 bg-white/70 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.24em] text-ink/60"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-brand-purple to-brand-teal" />
                        Book a Free Demo
                    </p>
                    <h2 className="mt-6 text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-[44px]">
                        Experience Work Island <span className="text-gradient">Before You Commit.</span>
                    </h2>
                    <p className="mt-4 max-w-md text-sm font-medium leading-relaxed text-ink/55 md:text-base">
                        Visit our workspace, explore the available options and find the plan that
                        fits the way you work.
                    </p>

                    <div className="mt-9 space-y-4">
                        {BENEFITS.map((benefit, i) => (
                            <motion.div
                                key={benefit.id}
                                initial={{ opacity: 0, x: -14 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.55, ease: EASE, delay: 0.15 + i * 0.09 }}
                                data-testid={`demo-benefit-${benefit.id}`}
                                className="group flex items-center gap-3.5"
                            >
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-purple/10 via-brand-blue/10 to-brand-teal/10 text-brand-blue transition-transform duration-300 group-hover:-translate-y-0.5">
                                    <benefit.icon size={17} strokeWidth={2.2} />
                                </span>
                                <span className="text-[15px] font-bold text-ink/80">{benefit.label}</span>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-10 flex items-center gap-5 border-t border-slate-200/70 pt-6">
                        <p className="text-[13px] font-semibold text-ink/45">Prefer to talk?</p>
                        <a data-testid="demo-call-link" href={contact.telUrl} onClick={() => trackClick("call", "demo-booking")} className="inline-flex items-center gap-2 text-[13px] font-bold text-ink/70 transition-colors hover:text-ink">
                            <Phone size={14} strokeWidth={2.4} className="text-brand-blue" />
                            {contact.phoneDisplay}
                        </a>
                        <a
                            data-testid="demo-whatsapp-link"
                            href={contact.waLink("Hi Work Island, I'd like to book a free demo!")}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackClick("whatsapp", "demo-booking")}
                            className="inline-flex items-center gap-2 text-[13px] font-bold text-ink/70 transition-colors hover:text-ink"
                        >
                            <MessageCircle size={14} strokeWidth={2.4} className="text-brand-teal" />
                            WhatsApp
                        </a>
                    </div>
                </motion.div>

                {/* RIGHT — booking form */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: EASE, delay: 0.12 }}
                    className="rounded-3xl border border-slate-200/80 bg-white p-7 shadow-[0_30px_70px_-35px_rgba(22,50,79,0.35)] sm:p-9"
                >
                    <AnimatePresence mode="wait">
                        {status === "success" && booked ? (
                            <motion.div
                                key="success"
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5, ease: EASE }}
                                aria-live="polite"
                                data-testid="demo-success"
                                className="py-4 text-center"
                            >
                                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-brand-purple via-brand-blue to-brand-teal text-white shadow-[0_16px_36px_-14px_rgba(43,92,171,0.55)]">
                                    <CheckCircle2 size={30} strokeWidth={2.2} />
                                </span>
                                <h3 className="mt-6 text-2xl font-bold tracking-tight text-ink">
                                    Your Demo Request Is Confirmed.
                                </h3>
                                <p className="mx-auto mt-3 max-w-sm text-sm font-medium leading-relaxed text-ink/55">
                                    We've received your request. Our team will contact you shortly
                                    to confirm your visit.
                                </p>
                                <div className="mx-auto mt-7 max-w-sm divide-y divide-slate-100 rounded-2xl border border-slate-200/80 bg-slate-50/60 text-left">
                                    {[
                                        ["Name", booked.name],
                                        ["Demo Date", fmtLong(booked.date)],
                                        ["Seats Required", `${booked.seats}`],
                                    ].map(([label, value]) => (
                                        <div key={label} className="flex items-center justify-between px-5 py-3.5">
                                            <span className="text-[11px] font-bold uppercase tracking-wider text-ink/40">{label}</span>
                                            <span className="text-sm font-bold text-ink">{value}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                                    <a
                                        data-testid="demo-success-whatsapp"
                                        href={contact.waLink("Hi Work Island, I just booked a free demo!")}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => trackClick("whatsapp", "demo-booking")}
                                        className="btn-gradient inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold text-white"
                                    >
                                        <MessageCircle size={16} strokeWidth={2.4} />
                                        WhatsApp Work Island
                                    </a>
                                    <a
                                        data-testid="demo-success-call"
                                        href={contact.telUrl}
                                        onClick={() => trackClick("call", "demo-booking")}
                                        className="inline-flex items-center gap-2 rounded-full border border-slate-300/90 bg-white px-6 py-3.5 text-sm font-bold text-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-ink/30"
                                    >
                                        <Phone size={16} strokeWidth={2.4} className="text-brand-blue" />
                                        Call Work Island
                                    </a>
                                </div>
                            </motion.div>
                        ) : status === "duplicate" ? (
                            <motion.div
                                key="duplicate"
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5, ease: EASE }}
                                aria-live="polite"
                                data-testid="demo-duplicate"
                                className="py-4 text-center"
                            >
                                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                                    <Info size={30} strokeWidth={2.2} />
                                </span>
                                <h3 className="mt-6 text-2xl font-bold tracking-tight text-ink">
                                    You've already booked a demo with Work Island.
                                </h3>
                                <p className="mx-auto mt-3 max-w-sm text-sm font-medium leading-relaxed text-ink/55">
                                    For further assistance or to enquire about our monthly workspace
                                    plans, please contact our team.
                                </p>
                                <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                                    <a
                                        data-testid="demo-duplicate-call"
                                        href={contact.telUrl}
                                        onClick={() => trackClick("call", "demo-booking")}
                                        className="btn-gradient inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold text-white"
                                    >
                                        <Phone size={16} strokeWidth={2.4} />
                                        Call Now
                                    </a>
                                    <a
                                        data-testid="demo-duplicate-whatsapp"
                                        href={contact.waLink("Hi Work Island, I need help with my demo booking!")}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => trackClick("whatsapp", "demo-booking")}
                                        className="inline-flex items-center gap-2 rounded-full border border-slate-300/90 bg-white px-6 py-3.5 text-sm font-bold text-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-ink/30"
                                    >
                                        <MessageCircle size={16} strokeWidth={2.4} className="text-brand-teal" />
                                        WhatsApp Us
                                    </a>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.form
                                key="form"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.4 }}
                                onSubmit={submit}
                                noValidate
                                data-testid="demo-form"
                            >
                                <h3 className="text-lg font-bold text-ink">Book your free demo visit</h3>
                                <p className="mt-1 text-[13px] font-medium text-ink/45">
                                    We usually confirm within a couple of hours.
                                </p>

                                <div className="mt-6 space-y-4">
                                    <div>
                                        <label htmlFor="demo-name" className={labelCls}>
                                            Name <span className="text-brand-purple">*</span>
                                        </label>
                                        <input
                                            id="demo-name"
                                            data-testid="demo-input-name"
                                            value={form.name}
                                            onChange={(e) => set("name", e.target.value)}
                                            placeholder="e.g. Rahul Sharma"
                                            className={inputCls}
                                            autoComplete="name"
                                            aria-describedby={errors.name ? "demo-name-error" : undefined}
                                        />
                                        {errors.name && <p id="demo-name-error" role="alert" className="mt-1.5 text-xs font-semibold text-rose-600">{errors.name}</p>}
                                    </div>

                                    <div>
                                        <label htmlFor="demo-company" className={labelCls}>
                                            Company Name <span className="font-medium normal-case text-ink/30">(optional)</span>
                                        </label>
                                        <input
                                            id="demo-company"
                                            data-testid="demo-input-company"
                                            value={form.company}
                                            onChange={(e) => set("company", e.target.value)}
                                            placeholder="e.g. Sharma Ventures"
                                            className={inputCls}
                                            autoComplete="organization"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="demo-mobile" className={labelCls}>
                                                Mobile Number <span className="text-brand-purple">*</span>
                                            </label>
                                            <input
                                                id="demo-mobile"
                                                data-testid="demo-input-mobile"
                                                value={form.mobile}
                                                onChange={(e) => set("mobile", e.target.value)}
                                                placeholder="98765 43210"
                                                className={inputCls}
                                                inputMode="tel"
                                                autoComplete="tel"
                                                aria-describedby={errors.mobile ? "demo-mobile-error" : undefined}
                                            />
                                            {errors.mobile && <p id="demo-mobile-error" role="alert" className="mt-1.5 text-xs font-semibold text-rose-600">{errors.mobile}</p>}
                                        </div>
                                        <div>
                                            <label htmlFor="demo-email" className={labelCls}>
                                                Email <span className="font-medium normal-case text-ink/30">(optional)</span>
                                            </label>
                                            <input
                                                id="demo-email"
                                                data-testid="demo-input-email"
                                                type="email"
                                                value={form.email}
                                                onChange={(e) => set("email", e.target.value)}
                                                placeholder="you@example.com"
                                                className={inputCls}
                                                autoComplete="email"
                                                aria-describedby={errors.email ? "demo-email-error" : undefined}
                                            />
                                            {errors.email && <p id="demo-email-error" role="alert" className="mt-1.5 text-xs font-semibold text-rose-600">{errors.email}</p>}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="demo-seats" className={labelCls}>
                                                Seats Required <span className="text-brand-purple">*</span>
                                            </label>
                                            <div className="flex items-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50/60 transition-all duration-300 focus-within:border-brand-blue/50 focus-within:bg-white focus-within:ring-4 focus-within:ring-brand-blue/10">
                                                <button
                                                    type="button"
                                                    data-testid="demo-seats-minus"
                                                    aria-label="Decrease seats"
                                                    onClick={() => stepSeats(-1)}
                                                    className="flex h-[50px] w-11 items-center justify-center text-ink/50 transition-colors hover:text-ink active:scale-95"
                                                >
                                                    <Minus size={15} strokeWidth={2.6} />
                                                </button>
                                                <input
                                                    id="demo-seats"
                                                    data-testid="demo-input-seats"
                                                    value={form.seats}
                                                    onChange={(e) => {
                                                        const v = parseInt(e.target.value.replace(/\D/g, ""), 10);
                                                        set("seats", isNaN(v) ? "" : Math.min(50, Math.max(1, v)));
                                                    }}
                                                    inputMode="numeric"
                                                    aria-describedby={errors.seats ? "demo-seats-error" : undefined}
                                                    className="w-full border-0 bg-transparent text-center text-sm font-bold text-ink focus:outline-none"
                                                />
                                                <button
                                                    type="button"
                                                    data-testid="demo-seats-plus"
                                                    aria-label="Increase seats"
                                                    onClick={() => stepSeats(1)}
                                                    className="flex h-[50px] w-11 items-center justify-center text-ink/50 transition-colors hover:text-ink active:scale-95"
                                                >
                                                    <Plus size={15} strokeWidth={2.6} />
                                                </button>
                                            </div>
                                            {errors.seats && <p id="demo-seats-error" role="alert" className="mt-1.5 text-xs font-semibold text-rose-600">{errors.seats}</p>}
                                        </div>
                                        <div>
                                            <label htmlFor="demo-date" className={labelCls}>
                                                Preferred Demo Date <span className="text-brand-purple">*</span>
                                            </label>
                                            <input
                                                id="demo-date"
                                                data-testid="demo-input-date"
                                                type="date"
                                                min={todayIso}
                                                value={form.date}
                                                onChange={(e) => set("date", e.target.value)}
                                                className={inputCls}
                                                aria-describedby={errors.date ? "demo-date-error" : "demo-date-availability"}
                                            />
                                            {errors.date ? (
                                                <p id="demo-date-error" role="alert" className="mt-1.5 text-xs font-semibold text-rose-600">{errors.date}</p>
                                            ) : (
                                                form.date &&
                                                dayAvail && (
                                                    <p id="demo-date-availability" aria-live="polite" data-testid="demo-date-availability" className={`mt-1.5 inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${statusStyle(dayAvail.status)}`}>
                                                        {dayAvail.blocked
                                                            ? "Closed on this date"
                                                            : dayAvail.status === "full"
                                                              ? "Fully booked for this date"
                                                              : `${dayAvail.remaining} of ${dayAvail.max || MAX_DEMOS_PER_DAY} slots available`}
                                                    </p>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    {/* next-days availability (live from backend) */}
                                    {Array.isArray(days) && days.length > 0 && (
                                        <div data-testid="demo-availability-strip">
                                            <p className={labelCls}>Next few days</p>
                                            <div className="flex flex-wrap gap-2">
                                                {days.map((d) => {
                                                    const dt = new Date(`${d.date}T00:00:00`);
                                                    const active = form.date === d.date;
                                                    return (
                                                        <button
                                                            key={d.date}
                                                            type="button"
                                                            data-testid={`demo-day-${d.date}`}
                                                            disabled={d.status === "full"}
                                                            onClick={() => set("date", d.date)}
                                                            className={`flex min-w-[64px] flex-col items-center rounded-xl border px-3 py-2 transition-all duration-300 ${
                                                                d.status === "full"
                                                                    ? "cursor-not-allowed border-slate-100 bg-slate-50 opacity-45"
                                                                    : active
                                                                      ? "border-transparent bg-gradient-to-br from-brand-purple via-brand-blue to-brand-teal text-white shadow-md"
                                                                      : "border-slate-200 bg-white text-ink hover:-translate-y-0.5 hover:border-brand-blue/40"
                                                            }`}
                                                        >
                                                            <span className={`text-[9px] font-bold uppercase tracking-wider ${active ? "text-white/80" : "text-ink/40"}`}>
                                                                {dt.toLocaleDateString("en-IN", { weekday: "short" })}
                                                            </span>
                                                            <span className="text-sm font-bold leading-tight">{dt.getDate()}</span>
                                                            <span className={`text-[9px] font-bold ${active ? "text-white/85" : d.status === "full" ? "text-rose-500" : d.status === "limited" ? "text-amber-600" : "text-brand-teal"}`}>
                                                                {d.blocked ? "Closed" : d.status === "full" ? "Full" : `${d.remaining} left`}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    data-testid="demo-submit-btn"
                                    disabled={status === "submitting" || dayAvail?.status === "full"}
                                    className="btn-gradient group mt-7 flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-[15px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {status === "submitting" ? "Booking your demo..." : "Book My Free Demo"}
                                    {status !== "submitting" && (
                                        <ArrowRight size={16} strokeWidth={2.6} className="transition-transform duration-300 group-hover:translate-x-1" />
                                    )}
                                </button>
                                <p className="mt-3.5 text-center text-[11.5px] font-semibold text-ink/45">
                                    No daily seat booking — we offer monthly memberships only.
                                </p>
                                <p className="mt-1.5 text-center text-[11px] font-medium text-ink/35">
                                    Your details are used only to contact you regarding your demo request.
                                </p>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </section>
    );
}
